import React, {useEffect, useMemo, useState} from 'react';
import {Icons, NA} from '@/constants/defaults';
import {TextInput, SelectInput, Icon, Text, Button} from '@/ui-kits';
import {SelectInputitem, DataTableColumn, Role, RoleAccessModule, GetAccessControlAccessModulesRequestPayload, UpdateRoleRequestPayload} from '@/interface';
import {DataTable, AssignedAdminModal, AccessPermissionsModal} from '@/components';
import {cn, formatName} from '@/utils/common_functions';
import { useSelector, useDispatch } from 'react-redux';
import { accessControlRolesSelector, accessControlAccessModulesSelector, accessControlCurrentPageSelector, accessControlTotalPagesSelector, accessControlTotalResultsSelector, appAdminRoles, userFailureSelector, userSuccessSelector } from '@/services/redux/selectors';
import { getAccessControlAccessModulesRequest, getAccessControlRolesRequest, updateRoleRequest, getAdminRolesRequest, resetUserMessage } from '@/services/redux/slices';
import { useToast } from '@/hooks';
import { ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes } from '@/utils/getMessages';


enum Tab {
  Roles,
  Access,
}

type TabItem = {id: Tab; label: string; icon: keyof typeof Icons};

const tabs: TabItem[]  = [
  {
    id: Tab.Roles,
    label: 'Roles',
    icon: 'roles',
  },
  {
    id: Tab.Access,
    label: 'Access Module',
    icon: 'access_modules',
  },
];

export const AccessControlScreen: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==============================
  // redux selectors
  // ==============================
  const roles = useSelector(accessControlRolesSelector);
  const accessModules = useSelector(accessControlAccessModulesSelector);
  const currentPage = useSelector(accessControlCurrentPageSelector);
  const totalPages = useSelector(accessControlTotalPagesSelector);
  const totalResults = useSelector(accessControlTotalResultsSelector);
  const userFailure = useSelector(userFailureSelector) as ErrorCodes;
  const userSuccess = useSelector(userSuccessSelector) as SuccessCodes;

  const roleFilterOptions = useSelector(appAdminRoles);

  // ==============================
  // states
  // ==============================
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [selectedRoleFilter, setSelectedRoleFilter] = useState<SelectInputitem | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.Roles);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [newRoleName, setNewRoleName] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isAssignedAdminsModalOpen, setIsAssignedAdminsModalOpen] = useState(false);
  const [isAccessPermissionsModalOpen, setIsAccessPermissionsModalOpen] = useState(false);
  const [selectedRoleForAccess, setSelectedRoleForAccess] = useState<Role | null>(null);


  // ==============================
  // functions
  // ==============================
  function resetFilter() {
    setPage(1);
    setSearch('');
    setSelectedRoleFilter(null);
  }

  const handleEdit = (role: Role) => {
    setEditingId(role.id);
    setNewRoleName(role.name);
  };

  const handleSave = (roleId: number) => {
    const payload: UpdateRoleRequestPayload = {
      id: roleId,
      name: newRoleName,
    };
    dispatch(updateRoleRequest(payload));
    setEditingId(null);
    setNewRoleName('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setNewRoleName('');
  };

  const changeTab = (tab: TabItem) => {
    setActiveTab(tab.id)
    resetFilter();
  }
  
  // ==============================
  // memoized functions
  // ==============================
  const roleColumns: DataTableColumn<Role>[] = useMemo(() => {
    return [
      {
        name: 'roleId',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ROLE ID</Text>,
        render: (row) => (
          <Text variant="free" className="font-FigtreeSemiBold text-base text-ui_input_border_dark">
            {row?.id ?? NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {
          minWidth: '120px',
        },
        align: 'left',
      },
      {
        name: 'roleName',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ROLE NAME</Text>,
        render: (row) => {
          const isEditing = editingId === row.id;
          if (isEditing) {
            return (
              <TextInput
                value={newRoleName ?? row.name}
                onChangeText={value => setNewRoleName(value)}
                wrapperClassName="border-[1px] w-full max-w-[200px] -ml-2"
                className="text-base"
                name="name"
              />
            );
          }
          return (
            <Text variant="free" className="font-FigtreeSemiBold text-base text-ui_input_border_dark">
              {row?.name ?? NA}
            </Text>
          );
        },
        headerAlign: 'left',
        width: {
          minWidth: '150px',
        },
        align: 'left',
      },
      {
        name: 'assignedAdmins',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ASSIGNED ADMINS</Text>,
        headerClassName: 'whitespace-nowrap -translate-x-8 sm:translate-x-0 lg:-translate-x-6 2xl:-translate-x-6',
        render: (row) => {
          return (
            <div className="flex items-center">
              <button
                onClick={() => {
                  setSelectedRole(row);
                  setIsAssignedAdminsModalOpen(true);
                }}
                className="px-6 py-1.5 border-[1px] rounded-lg border-ui_input_border text-ui_input_border_dark font-FigtreeSemiBold text-base ml-6 md:ml-8 lg:ml-6 hover:bg-ui_sky_blue hover:border-ui_blue transition-colors cursor-pointer">
                {row?.admins?.length ?? 0}
              </button>
            </div>
          );
        },
        headerAlign: 'left',
        width: {
          minWidth: '150px',
        },
        align: 'left',
      },
      {
        name: 'accessModules',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACCESS MODULES</Text>,
        render: (row) => {
          return (
            <div className="flex items-center">
              <button
                onClick={() => {
                  setSelectedRoleForAccess(row);
                  setIsAccessPermissionsModalOpen(true);
                }}
                className="px-6 py-1.5 border-[1px] rounded-lg border-ui_input_border text-ui_input_border_dark font-FigtreeSemiBold text-base ml-6 4xl:ml-8 2xl:ml-9 lg:ml-6 hover:bg-ui_sky_blue hover:border-ui_blue transition-colors cursor-pointer">
                {row?.access_modules?.length ?? 0}
              </button>
            </div>
          );
        },
        headerAlign: 'left',
        width: {
          minWidth: '200px',
        },
        align: 'right',
      },
      {
        name: 'actions',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACTIONS</Text>,
        width: {
          minWidth: '100px',
        },
        headerAlign: 'left',
        align: 'left',
        render: (row) => {
          const isEditing = editingId === row.id;

          if (isEditing) {
            return (
              <div className="flex items-center gap-3">
                <button onClick={() => handleSave(row.id)} className="p-1 hover:opacity-70 transition-opacity">
                  <Icon type="green_tick_btn" className="size-5" />
                </button>
                <button onClick={handleCancel} className="p-1 hover:opacity-70 transition-opacity">
                  <Icon type="cross_btn_red" className="size-5" />
                </button>
              </div>
            );
          }

          // Read-only mode: show only edit button for all rows
          return (
            <div className="flex items-center gap-3">
              <button onClick={() => handleEdit(row)} className="p-1 hover:opacity-70 transition-opacity">
                <Icon type="edit_input" className="size-4 ml-6 4xl:ml-4 2xl:ml-10 lg:ml-8" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [editingId, newRoleName]);

  const accessModuleColumns: DataTableColumn<RoleAccessModule>[] = useMemo(() => {
    return [
      {
        name: 'accessId',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACCESS ID</Text>,
        render: (row) => (
          <Text variant="free" className="font-FigtreeSemiBold text-base text-ui_input_border_dark">
            {row?.id ?? NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {
          minWidth: '120px',
        },
        align: 'left',
      },
      {
        name: 'accessModuleName',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACCESS MODULE NAME</Text>,
        render: (row) => (
          <Text variant="free" className="font-FigtreeSemiBold text-base text-ui_input_border_dark">
            {formatName(row?.name) ?? NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {
          minWidth: '200px',
        },
        align: 'left',
      },
      {
        name: 'assignedRole',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ASSIGNED ROLE</Text>,
        render: (row) => (
          <Text variant="free" className="font-FigtreeSemiBold text-base text-ui_input_border_dark">
            {row?.role?.name ?? NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {
          minWidth: '150px',
        },
        align: 'left',
      },
    ];
  }, []);

  // ==============================
  // side effects
  // ==============================

  // fetch data
  useEffect(() => {
    if (activeTab === Tab.Roles) {
      dispatch(getAccessControlRolesRequest({page:1, search_text: search}));
    } else {
      const payload: GetAccessControlAccessModulesRequestPayload = {
        page,
        search_text: search,
      };
      if (selectedRoleFilter?.id) {
        payload.role = selectedRoleFilter.id;
      }
      dispatch(getAccessControlAccessModulesRequest(payload));
    }
  }, [page, search, selectedRoleFilter?.id, activeTab]);

  // Clear any stale messages when component mounts or tab changes
  useEffect(() => {
    dispatch(resetUserMessage());
  }, [activeTab, dispatch]);

  // error & failure handler
  useEffect(() => {
    if (userFailure) {
      showToast(getErrorMessage(userFailure), 'failure');
      dispatch(resetUserMessage());
    }
    if (userSuccess) {
      if (userSuccess === 'S-10064') {
        // update the role filter options
        showToast(getSuccessMessage(userSuccess), 'success');
        dispatch(getAdminRolesRequest());
        dispatch(resetUserMessage());
      }
    }

    // Cleanup: clear messages when component unmounts
    return () => {
      dispatch(resetUserMessage());
    };
  }, [userFailure, userSuccess, dispatch, showToast]);

  // ==============================
  // render
  // ==============================
  return (
    <div className="flex flex-col grow p-4 sm:p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <Text variant="free" className="font-FigtreeBold text-2xl sm:text-3xl lg:text-3xl text-ui_blue mb-1">
          Role-Based Access Control
        </Text>
        <Text variant="free" className="font-FigtreeRegular text-base sm:text-xl lg:text-xl text-ui_blue opacity-80">
          Master list of roles with admin assignments and access.
        </Text>
      </div>

      {/* Search and Filter Bar */}
      <div className="w-full mt-4 mb-6">
        <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3 lg:gap-4">
          {/* Search - flexible width that can shrink */}
          <div className="flex-1 min-w-[calc(100%-150px)] sm:min-w-[280px] sm:max-w-[25rem]">
            <TextInput
              value={search}
              onChangeText={setSearch}
              wrapperClassName="border-[1px] w-full"
              className="w-full"
              inputClassName=""
              leftIcon="search_blue"
              iconClassName="ml-3"
              name="search"
              placeholder={activeTab === Tab.Roles ? "Search by Role Name, Role ID" : "Search by Name or ID"}
            />
          </div>

          {/* Controls group - keep tight spacing */}
          {(activeTab === Tab.Access || search) && (
            <>
              {activeTab === Tab.Access && (
                <div className="w-[140px] min-w-[140px] max-w-[140px] sm:w-[260px] sm:min-w-[260px] sm:max-w-[260px] lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px] flex-shrink-0">
                  <SelectInput
                    className="w-full"
                    wrapperClassName="border w-full"
                    name="roleFilter"
                    placeholder="All Roles"
                    options={roleFilterOptions}
                    value={selectedRoleFilter?.id || ''}
                    onChange={(item) => {
                      setSelectedRoleFilter(item);
                      // reset the page to 1
                      setPage(1);
                    }}
                  />
                </div>
              )}

              {(search || selectedRoleFilter) && (
                <Button
                  title="Reset Filters"
                  variant="secondary"
                  className="w-fit sm:w-auto sm:min-w-[120px]"
                  onClick={resetFilter}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-12 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => changeTab(tab)}
            className={cn(
              'flex items-center gap-2 transition-colors',
              activeTab === tab.id ? 'px-6 py-2 rounded-lg border-[1px] border-ui_blue min-w-[110px]' : '',
            )}>
            <Icon type={tab.icon} className="size-5 -ml-2" />
            <Text variant="free" className={cn('font-FigtreeSemiBold text-base  whitespace-nowrap', activeTab === tab.id ? 'text-ui_blue' : 'text-ui_input_border_dark')}>
              {tab.label}
            </Text>
          </button>
        ))}
      </div>

      {/* Table */}
      {activeTab === Tab.Roles && (
        <DataTable
          data={roles}
          columns={roleColumns}
          totalPages={totalPages}
          totalResult={totalResults}
          currentPage={currentPage}
          onPageChange={setPage}
          errorMessage={userFailure === 'E-10044' ? getErrorMessage(userFailure) : totalResults === 0 ? 'No data available' : undefined}
          showFooter={false}
        />
      )}

      {activeTab === Tab.Access && (
        <div className="w-full 2xl:max-w-[85%]">
          <DataTable
            data={accessModules}
            columns={accessModuleColumns}
            totalPages={totalPages}
            totalResult={totalResults}
            currentPage={currentPage}
            onPageChange={setPage}
            errorMessage={userFailure === 'E-10044' ? getErrorMessage(userFailure) : totalResults === 0 ? 'No data available' : undefined}
          />
        </div>
      )}

      {/* Assigned Admin Modal */}
      {selectedRole && (
        <AssignedAdminModal
          isOpen={isAssignedAdminsModalOpen}
          onClose={() => {
            setIsAssignedAdminsModalOpen(false);
            setSelectedRole(null);
          }}
          roleName={selectedRole.name}
          assignedAdmins={selectedRole.admins || []}
        />
      )}

      {/* Access Permissions Modal */}
      {selectedRoleForAccess && (
        <AccessPermissionsModal
          isOpen={isAccessPermissionsModalOpen}
          onClose={() => {
            setIsAccessPermissionsModalOpen(false);
            setSelectedRoleForAccess(null);
          }}
          roleName={selectedRoleForAccess.name}
          accessPermissions={selectedRoleForAccess.access_modules || []}
        />
      )}
    </div>
  );
};
